import { formatDate } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, LOCALE_ID, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AiAssistantPanelService } from '@simOn/layout';
import { SpaceFacade } from '@simOn/space';
import { SimButtonModule } from '@simOn/ui/sim-button';
import { SimFloatingWindowHandleDirective } from '@simOn/ui/sim-floating-window';
import { SimHistoryPopoverComponent, SimHistoryPopoverGroup } from '@simOn/ui/sim-history-popover';
import { SimIconModule } from '@simOn/ui/sim-icon';
import { EllipsisTooltipDirective, SimTooltipComponent } from '@simOn/ui/sim-tooltip';
import { AI_AGENT_ACCESS_CONTACT_EMAIL } from '@simOn/utils/tokens';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { map } from 'rxjs';
import {
  agentAccessMailto,
  AgentChatAction,
  AgentConversationGroupKey,
  conversationDate,
  formatTokenCount,
  groupConversations
} from '../../model';
import { AgentChatStore } from '../../store';
import { AgentAccessSettingsComponent } from '../agent-access-settings/agent-access-settings.component';
import { AgentAccessStateComponent } from '../agent-access-state/agent-access-state.component';
import { AgentMessageComposerComponent } from '../agent-message-composer/agent-message-composer.component';
import { AgentMessageListComponent } from '../agent-message-list/agent-message-list.component';

type PanelView = 'chat' | 'settings';

/** Pill at the right of the footer summarising the tokens left; opens the access view. */
interface StatusChip {
  icon: string;
  label: string;
  blocked: boolean;
}

/**
 * Body of the AI assistant chat: the header with the new chat, history, access and close buttons (also the drag
 * handle of the floating window), the chat (or the conversation history / the access view), the message input
 * and the footer with the selected space and the tokens left. Every opening of the panel starts a new chat. While the assistant is blocked (not enabled by the office, no tokens left) an empty
 * draft shows the access view instead of the chat; open conversations stay readable with a notice.
 */
@Component({
  selector: 'ai-agent-chat-panel',
  imports: [
    SimButtonModule,
    SimIconModule,
    SimTooltipComponent,
    EllipsisTooltipDirective,
    SimFloatingWindowHandleDirective,
    AgentAccessSettingsComponent,
    AgentAccessStateComponent,
    SimHistoryPopoverComponent,
    AgentMessageListComponent,
    AgentMessageComposerComponent
  ],
  templateUrl: './agent-chat-panel.component.html',
  styleUrl: './agent-chat-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AgentChatPanelComponent implements OnInit {
  protected readonly store = inject(AgentChatStore);
  private readonly _panel = inject(AiAssistantPanelService);
  private readonly _spaceFacade = inject(SpaceFacade);
  private readonly _oidc = inject(OidcSecurityService);
  private readonly _locale = inject(LOCALE_ID);
  protected readonly view = signal<PanelView>('chat');
  /** The history popover over the chat. */
  protected readonly historyOpen = signal(false);

  protected readonly title = $localize`:@@AI_AGENT_TITLE:AI Assistant`;
  protected readonly historyLabel = $localize`:@@AI_AGENT_CHAT_HISTORY:Chat history`;
  protected readonly closeHistoryLabel = $localize`:@@AI_AGENT_CLOSE_HISTORY:Close history`;
  protected readonly currentLabel = $localize`:@@AI_AGENT_CURRENT:Current`;
  protected readonly noChatsTitle = $localize`:@@AI_AGENT_NO_CHATS:No chats yet`;
  protected readonly noChatsText = $localize`:@@AI_AGENT_NO_CHATS_TEXT:Your conversations will show up here after you send your first message.`;
  protected readonly startChatLabel = $localize`:@@AI_AGENT_START_CHAT:Start a chat`;
  protected readonly sendingLabel = $localize`:@@AI_AGENT_SENDING:Sending…`;
  private readonly _untitledLabel = $localize`:@@AI_AGENT_UNTITLED_CONVERSATION:New conversation`;
  private readonly _groupLabels: Record<AgentConversationGroupKey, string> = {
    today: $localize`:@@CALENDAR_TODAY:Today`,
    yesterday: $localize`:@@AI_AGENT_YESTERDAY:Yesterday`,
    older: $localize`:@@AI_AGENT_OLDER:Older`
  };
  protected readonly backToChatLabel = $localize`:@@AI_AGENT_BACK_TO_CHAT:Back to chat`;
  protected readonly newConversationLabel = $localize`:@@AI_AGENT_NEW_CHAT:New chat`;
  protected readonly accessLabel = $localize`:@@AI_AGENT_ACCESS_AND_TOKENS:Access & tokens`;
  protected readonly closeLabel = $localize`:@@GENERAL_CLOSE: Close `;
  protected readonly dismissLabel = $localize`:@@AI_AGENT_DISMISS:Dismiss`;
  protected readonly cardsStillWorkLabel = $localize`:@@AI_AGENT_CARDS_STILL_WORK:Cards from earlier chats still work.`;
  /** Office contact from the environment (`aiAgent.accessContactEmail`). */
  protected readonly contactEmail = inject(AI_AGENT_ACCESS_CONTACT_EMAIL);
  private readonly _tokensLeftLabel = $localize`:@@AI_AGENT_TOKENS_LEFT:tokens left`;
  private readonly _notEnabledLabel = $localize`:@@AI_AGENT_STATUS_NOT_ENABLED:Not enabled`;

  /** Shown in the access view and on the "not enabled" plate. */
  protected readonly userEmail = toSignal(
    this._oidc.userData$.pipe(map(({ userData }) => (userData?.email as string | undefined) ?? null)),
    { initialValue: null }
  );
  protected readonly spaceName = toSignal(
    this._spaceFacade.selectedApartment$.pipe(map((space) => space?.name ?? '')),
    {
      initialValue: ''
    }
  );
  /** A conversation is open (or its first message is on its way), as opposed to an empty draft. */
  protected readonly inConversation = computed(() => !!this.store.selectedConversationId() || this.store.hasMessages());
  /** History rows grouped by last activity: time for today and yesterday, the date for older ones. */
  protected readonly historyGroups = computed<SimHistoryPopoverGroup[]>(() =>
    groupConversations(this.store.conversations(), new Date()).map(({ key, items }) => ({
      label: this._groupLabels[key],
      items: items.map((conversation) => ({
        id: conversation.id,
        title: conversation.title || this._untitledLabel,
        meta: formatDate(conversationDate(conversation), key === 'older' ? 'shortDate' : 'shortTime', this._locale),
        busy: conversation.id === this.store.sendingConversationId()
      }))
    }))
  );
  /** "AI Assistant" on an empty draft, otherwise the title the backend generated from the first message. */
  protected readonly headerTitle = computed(() =>
    this.inConversation() ? this.store.chatTitle() || this.newConversationLabel : this.title
  );
  /** Not enabled: there is nothing to read, so a plate replaces the chat; out of tokens keeps the chat readable. */
  protected readonly blockedView = computed(() => this.store.blockedState() === 'notEnabled');
  protected readonly contactMailto = computed<string | null>(() => {
    const texts = this.store.accessTexts();
    return texts ? agentAccessMailto(this.contactEmail, texts) : null;
  });
  protected readonly statusChip = computed<StatusChip | null>(() => {
    const state = this.store.assistantState();
    const status = this.store.userStatus();
    if (!state || !status) return null;
    if (state === 'notEnabled') return { icon: 'lock', label: this._notEnabledLabel, blocked: true };
    const remaining = formatTokenCount(state === 'noTokens' ? 0 : status.remainingTokens, this._locale);
    return { icon: 'security', label: `${remaining} ${this._tokensLeftLabel}`, blocked: state === 'noTokens' };
  });

  ngOnInit(): void {
    // The launcher always opens a new chat; earlier ones are reachable from the history.
    this.store.startNewConversation();
    this.store.loadUserStatus();
    if (!this.store.conversationsLoaded()) this.store.loadConversations();
  }

  protected toggleHistory(): void {
    this.historyOpen.update((open) => !open);
  }

  protected closeHistory(): void {
    this.historyOpen.set(false);
  }

  protected showSettings(): void {
    this.historyOpen.set(false);
    this.view.set('settings');
  }

  protected showChat(): void {
    this.view.set('chat');
  }

  protected onRefreshStatus(): void {
    this.store.loadUserStatus();
  }

  protected onNewConversation(): void {
    this.store.startNewConversation();
    this.historyOpen.set(false);
    this.view.set('chat');
  }

  protected onSelectConversation(conversationId: string): void {
    if (conversationId !== this.store.selectedConversationId()) this.store.selectConversation(conversationId);
    this.historyOpen.set(false);
  }

  protected onDismissError(): void {
    this.store.clearError();
  }

  protected onSend(message: string): void {
    this.store.sendMessage(message);
  }

  protected onMessageRestored(): void {
    this.store.clearRestoredMessage();
  }

  protected onRetry(messageId: string): void {
    this.store.retryMessage(messageId);
  }

  protected onGoToPosition(action: AgentChatAction): void {
    this.store.goToPosition(action);
  }

  protected onAction(action: AgentChatAction): void {
    this.store.executeAction(action);
  }

  protected onClose(): void {
    this._panel.close();
  }
}
